import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../ui/Icon';
import { MobileSheet } from '../ui/MobileSheet';
import { MobileDiagnostica } from './MobileDiagnostica';
import { useAuthStore } from '../../stores/auth.store';
import { MOBILE_TABS, MOBILE_MORE, canSeeModule, isNavActive } from '../../lib/nav';
import { isFrozen } from '../../lib/frozen';
import { useActionStream } from '../../features/oggi/useActionStream';
import { tasksSummaryQuery } from '../../features/tasks/tasksApi';
import { ModuleKey } from '@masamune/shared';

/**
 * Barra fissa in basso — la navigazione del telefono.
 *
 * Esiste solo sotto i 768px: sopra resta la sidebar e il desktop non cambia di
 * un pixel. Il nascondersi lo fa il CSS (mobile.css), non un matchMedia in JS,
 * così non c'è un istante in cui la barra lampeggia mentre React decide.
 *
 * Quattro voci più "Altro", decise con Lorenzo. "Altro" non è una sezione: è la
 * porta su tutte le altre, e si apre in un pannello che sale dal basso.
 *
 * Perché in basso: è l'unica fascia dello schermo che il pollice raggiunge
 * senza cambiare presa. Tutto quello che si tocca spesso vive qui.
 */
export function MobileTabBar() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  // Cambiare pagina chiude il pannello: se resta aperto dopo il tocco, sembra
  // che la navigazione non abbia funzionato.
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  // Conteggi vivi, gli stessi della sidebar: nessuna chiamata in più, la cache
  // di react-query è condivisa.
  const { counts } = useActionStream();
  const canTasks = canSeeModule(user, ModuleKey.TASKS);
  const { data: taskSummary } = useQuery({ ...tasksSummaryQuery('mine'), enabled: canTasks });

  if (!user) return null;

  const badgeFor = (key: string): number => {
    if (key === 'oggi') return counts.critical + counts.today;
    if (key === 'tasks') return (taskSummary?.dueToday ?? 0) + (taskSummary?.overdue ?? 0);
    return 0;
  };

  // I permessi filtrano qui, una volta: chi non vede un modulo non lo trova né
  // in barra né dentro "Altro".
  const tabs = MOBILE_TABS.filter((t) => canSeeModule(user, t.module));
  const moreGroups = MOBILE_MORE
    .map((g) => ({ ...g, items: g.items.filter((it) => canSeeModule(user, it.module)) }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      <nav className="m-tabbar" aria-label="Sezioni principali">
        {tabs.map((t) => {
          const active = isNavActive(location.pathname, t.to);
          const badge = badgeFor(t.key);
          const paused = isFrozen(t.module);
          return (
            <NavLink
              key={t.key}
              to={t.to}
              className="m-tab"
              aria-current={active ? 'page' : undefined}
              aria-label={t.label}
            >
              <span className="m-tab-ico">
                {/* Attiva = tratto più spesso, dentro una pastiglia piena.
                    Niente colore squillante: il peso fa da solo. */}
                <Icon name={t.icon} size={21} strokeWidth={active ? 2.25 : 1.75} />
                {badge > 0 && !paused && <span className="m-tab-badge">{badge > 99 ? '99+' : badge}</span>}
              </span>
              <span className="m-tab-lbl">{t.short ?? t.label}</span>
            </NavLink>
          );
        })}

        <button
          type="button"
          className="m-tab"
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          aria-current={moreOpen ? 'page' : undefined}
          onClick={() => setMoreOpen(true)}
        >
          <span className="m-tab-ico">
            <Icon name="grid" size={21} strokeWidth={moreOpen ? 2.25 : 1.75} />
          </span>
          <span className="m-tab-lbl">Altro</span>
        </button>
      </nav>

      <MobileSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Tutte le sezioni">
        {moreGroups.map((g) => (
          <div className="m-sheet-group" key={g.title}>
            <div className="m-sheet-group-label">{g.title}</div>
            {g.items.map((it) => (
              <NavLink
                key={it.key}
                to={it.to}
                className="m-sheet-row"
                aria-current={isNavActive(location.pathname, it.to) ? 'page' : undefined}
                onClick={() => setMoreOpen(false)}
              >
                <Icon name={it.icon} size={17} />
                <span className="m-sheet-row-lbl">{it.label}</span>
                {isFrozen(it.module)
                  ? <span className="m-sheet-row-tag">In pausa</span>
                  : <Icon name="chevronR" size={14} className="m-sheet-row-go" />}
              </NavLink>
            ))}
          </div>
        ))}
        {/* Cosa sta succedendo su questo telefono: serve a chi ripara, e sta in
            fondo perché a chi lavora non interessa. Vedi MobileDiagnostica.tsx. */}
        <div className="m-sheet-group">
          <div className="m-sheet-group-label">Questo telefono</div>
          <MobileDiagnostica />
        </div>

        {/* Chi è collegato, e la via d'uscita: in fondo, dove il pollice arriva. */}
        <div className="m-sheet-group">
          <div className="m-sheet-group-label">Sei collegato come</div>
          <div className="m-sheet-me">
            <span className="m-sheet-me-av">{user.initials}</span>
            <span className="m-sheet-me-txt">
              <strong>{user.firstName} {user.lastName}</strong>
              <em>{user.role.name}</em>
            </span>
          </div>
        </div>
      </MobileSheet>
    </>
  );
}
